import { useEffect, useRef } from 'react';
import { supabase } from '../lib/supabase';

/**
 * Process-global monotonic counter making every subscribed channel name unique
 * per effect-run. See the channel-name note in the effect below: `removeChannel`
 * is asynchronous (it round-trips an UNSUBSCRIBE and only drops the channel from
 * the client's registry after the server acks), so a remount that re-subscribes
 * before that ack lands would otherwise reuse the SAME topic — and
 * `supabase.channel(topic)` returns the EXISTING, already-subscribed channel,
 * making the subsequent `.on('postgres_changes', …)` throw
 * "cannot add `postgres_changes` callbacks … after `subscribe()`" and crash the
 * subtree. A per-mount nonce guarantees a fresh channel every time.
 */
let channelNonce = 0;

/**
 * Subscribe to Supabase Realtime Postgres changes on a SINGLE row and reconcile
 * the open editor inline — the source-agnostic fix for "an external/agent edit
 * to the item the user currently has open isn't visible until they leave and
 * re-open it".
 *
 * Unlike the agent-frame path (`useAgentWriteSubscription`), this listens to the
 * DATABASE, so it fires for ANY writer of the row — the real agent CLI, a second
 * browser tab, a server-side RPC — not just writes that flow through the IDE's
 * WebSocket frame bus. It is intentionally NOT gated on `isIDE()`.
 *
 * Behaviour:
 *  - Opens one channel per (`table`, `filterColumn`, `rowId`) triple and filters
 *    server-side to `<filterColumn>=eq.<rowId>` so the client only receives
 *    events for the open row. `filterColumn` defaults to `'id'` (the item's own
 *    row); pass a FK column (`document_id` / `work_item_id`) to subscribe to a
 *    child table such as `custom_attribute_values` / `work_item_property_values`
 *    keyed by the open item — the mechanism that makes external attribute edits
 *    refresh inline the same way title/body do.
 *  - On an `UPDATE` (or `INSERT`, defensively) it calls `onChange` — DEBOUNCED
 *    so a burst of rapid writes coalesces into a single reconcile. The caller's
 *    `onChange` is the existing `reloadFromServer()` reconcile, which already
 *    (a) defers when a user save is in flight and (b) only adopts server values
 *    into fields the user has NOT locally edited — so this never clobbers
 *    unsaved edits or the caret.
 *  - On a `DELETE` it calls `onDelete` immediately (no debounce — a delete is
 *    terminal; the caller raises the archive banner).
 *  - Unsubscribes + clears the pending debounce on unmount and before every
 *    re-subscribe (a changing `rowId`/`table`).
 *  - No-op when `rowId` is null/undefined (nothing open yet).
 *
 * Callbacks are held in refs so a fresh inline closure each render does NOT
 * churn the subscription — only `table` / `rowId` / `debounceMs` resubscribe.
 *
 * Realtime authorization runs the change payload through the table's RLS SELECT
 * policy as the subscribing user, so a client only ever receives events for
 * rows it is already allowed to read (documents / work_items are member-scoped).
 */
export function useRealtimeRowReload(options: {
  table:
    | 'documents'
    | 'work_items'
    | 'custom_attribute_values'
    | 'work_item_property_values';
  rowId: string | null | undefined;
  /**
   * Server-side filter column matched against `rowId`. Defaults to `'id'` (the
   * item's own row). Pass a FK (`document_id` / `work_item_id`) to subscribe to
   * a child value table keyed by the open item.
   */
  filterColumn?: string;
  onChange: () => void;
  onDelete?: () => void;
  /** Coalesce window for `onChange`, ms. Defaults to 250ms. */
  debounceMs?: number;
}): void {
  const {
    table,
    rowId,
    filterColumn = 'id',
    onChange,
    onDelete,
    debounceMs = 250
  } = options;

  const onChangeRef = useRef(onChange);
  onChangeRef.current = onChange;
  const onDeleteRef = useRef(onDelete);
  onDeleteRef.current = onDelete;

  useEffect(() => {
    if (!rowId) return;

    let debounceTimer: ReturnType<typeof setTimeout> | null = null;
    const scheduleChange = () => {
      if (debounceTimer) clearTimeout(debounceTimer);
      debounceTimer = setTimeout(() => {
        debounceTimer = null;
        onChangeRef.current();
      }, debounceMs);
    };

    // A unique channel name per (table,filterColumn,row) avoids collisions when
    // two editors (document + work item) are mounted, when an item subscribes to
    // both its own row and its child value table, and lets two opens of the same
    // row in quick succession each get their own clean channel. A per-mount
    // nonce is appended because `removeChannel` (in cleanup) is ASYNCHRONOUS —
    // it only drops the channel from the client's registry after the server
    // acks the UNSUBSCRIBE. Without the nonce, a remount that re-subscribes
    // before that ack lands reuses the same topic, and `supabase.channel(topic)`
    // returns the EXISTING, still-subscribed channel — so the `.on(…)` calls
    // below throw "cannot add `postgres_changes` callbacks … after `subscribe()`"
    // and crash the editor subtree (observed crashing the IDE-embedded
    // WorkItemDetail's PropertiesPanel into a never-recovering blank state).
    const filter = `${filterColumn}=eq.${rowId}`;
    const channel = supabase
      .channel(`row-reload:${table}:${filterColumn}:${rowId}:${++channelNonce}`)
      .on(
        'postgres_changes',
        {
          event: 'UPDATE',
          schema: 'public',
          table,
          filter
        },
        () => {
          scheduleChange();
        }
      )
      .on(
        'postgres_changes',
        {
          event: 'INSERT',
          schema: 'public',
          table,
          filter
        },
        () => {
          // Defensive: a row materialising under the open id is also a reason to
          // reconcile. For child value tables an INSERT is the COMMON case (a
          // property set for the first time), so this is load-bearing there.
          scheduleChange();
        }
      )
      .on(
        'postgres_changes',
        {
          event: 'DELETE',
          schema: 'public',
          table,
          filter
        },
        () => {
          if (onDeleteRef.current) {
            // The item's own row was deleted — terminal. Fire immediately and
            // drop any pending change reconcile (reconciling a deleted row is
            // pointless); the caller raises the archive banner.
            if (debounceTimer) {
              clearTimeout(debounceTimer);
              debounceTimer = null;
            }
            onDeleteRef.current();
            return;
          }
          // No onDelete handler: this is a child value-table subscription
          // (e.g. a property value cleared). A delete there is just another
          // reason to re-fetch the panel, so reconcile (debounced) like a
          // change. NOTE: under DEFAULT replica identity a DELETE payload only
          // carries the PK, so an FK filter rarely matches — INSERT/UPDATE are
          // the load-bearing events for child tables; this is a safety net.
          scheduleChange();
        }
      )
      .subscribe((status, err) => {
        // Observability for the live reconcile. A healthy subscription reaches
        // SUBSCRIBED; CHANNEL_ERROR / TIMED_OUT means the socket could not
        // authorize or reach the server — the most common cause being a
        // Realtime connection without the user's JWT (see
        // `applyRealtimeAuth` in lib/supabase.ts), which silently delivers no
        // postgres_changes for RLS-protected rows.
        if (status === 'CHANNEL_ERROR' || status === 'TIMED_OUT') {
          // eslint-disable-next-line no-console
          console.warn(
            `[useRealtimeRowReload] channel ${channel.topic} status=${status}`,
            err ?? ''
          );
        }
      });

    return () => {
      if (debounceTimer) {
        clearTimeout(debounceTimer);
        debounceTimer = null;
      }
      void supabase.removeChannel(channel);
    };
  }, [table, rowId, filterColumn, debounceMs]);
}
